import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useColorMode } from "@chakra-ui/react";

const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,800&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');`;

/* ─── CONTACT CHANNELS ─────────────────────────────────────────────────────── */
const CHANNELS = [
  {
    icon: "🤝",
    label: "Partnerships",
    email: "partners@grappl-tech.in",
    desc: "Explore collaborations, co-branded programs, or institutional tie-ups.",
    accent: "#028EC4",
  },
  {
    icon: "💼",
    label: "Sales",
    email: "sales@grappl-tech.in",
    desc: "Bulk enrolments, corporate training packages, and group pricing.",
    accent: "#F59E0B",
  },
  {
    icon: "🛟",
    label: "Support",
    email: "support@grappl-tech.in",
    desc: "Facing an issue with a course or platform? We've got you covered.",
    accent: "#10B981",
  },
  {
    icon: "🚀",
    label: "Careers",
    email: "careers@grappl-tech.in",
    desc: "Join the GrapplTech team. We're always looking for driven builders.",
    accent: "#6366F1",
  },
  {
    icon: "🗂️",
    label: "Portfolios",
    email: "portfolios@grappl-tech.in",
    desc: "Submit your learner portfolio for review, showcasing, or certification.",
    accent: "#EC4899",
  },
  {
    icon: "✅",
    label: "Verification",
    email: "verification@grappl-tech.in",
    desc: "Verify a GrapplTech certificate or learner completion record.",
    accent: "#14B8A6",
  },
];

/* ─── ICONS ────────────────────────────────────────────────────────────────── */
const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const CheckTinyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" />
  </svg>
);

/* ─── COPY BUTTON ──────────────────────────────────────────────────────────── */
function CopyButton({ text, isDark }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      title="Copy email"
      style={{
        background: copied
          ? (isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.1)")
          : (isDark ? "rgba(255,255,255,0.08)" : "#F3F4F6"),
        border: "none",
        borderRadius: 7,
        padding: "5px 8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: copied ? "#10B981" : (isDark ? "rgba(255,255,255,0.6)" : "#6B7280"),
        transition: "all 0.2s",
        flexShrink: 0,
      }}
    >
      {copied ? <CheckTinyIcon /> : <CopyIcon />}
    </button>
  );
}

/* ─── EMAIL CHANNEL CARD ───────────────────────────────────────────────────── */
function ChannelCard({ data, isDark }) {
  const cardBg     = isDark ? "#16161f"                : "#ffffff";
  const cardBorder = isDark ? "rgba(255,255,255,0.09)" : "#E5E7EB";
  const textPri    = isDark ? "rgba(255,255,255,0.92)" : "#111827";
  const textMuted  = isDark ? "rgba(255,255,255,0.50)" : "#6B7280";
  const emailBg    = isDark ? "rgba(255,255,255,0.05)" : "#F9FAFB";
  const emailBorder= isDark ? "rgba(255,255,255,0.09)" : "#E5E7EB";
  const shadow     = isDark ? "0 4px 18px rgba(0,0,0,0.3)" : "0 2px 10px rgba(0,0,0,0.06)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -4, boxShadow: `0 16px 40px rgba(0,0,0,0.15), 0 0 0 1.5px ${data.accent}44` }}
      transition={{ duration: 0.2 }}
      style={{
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: 18,
        padding: "24px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        boxShadow: shadow,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle top accent line */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, borderRadius: "18px 18px 0 0", background: data.accent, opacity: 0.7 }} />

      {/* Icon + Label */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: data.accent + "18",
          border: `1px solid ${data.accent}30`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, flexShrink: 0,
        }}>
          {data.icon}
        </div>
        <div>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: data.accent + "15", color: data.accent,
            fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
            textTransform: "uppercase", padding: "3px 9px", borderRadius: 999,
            border: `1px solid ${data.accent}30`,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: data.accent }} />
            {data.label}
          </span>
        </div>
      </div>

      {/* Description */}
      <p style={{ fontSize: 13.5, color: textMuted, lineHeight: 1.65, margin: 0 }}>
        {data.desc}
      </p>

      {/* Email row */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        background: emailBg, border: `1px solid ${emailBorder}`,
        borderRadius: 10, padding: "9px 12px",
      }}>
        <span style={{ color: data.accent, flexShrink: 0 }}><MailIcon /></span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: textPri, flex: 1, wordBreak: "break-all" }}>
          {data.email}
        </span>
        <CopyButton text={data.email} isDark={isDark} />
      </div>

      {/* mailto CTA */}
      <a
        href={`mailto:${data.email}`}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "transparent",
          border: `1px solid ${data.accent}55`,
          color: data.accent,
          fontSize: 12.5, fontWeight: 700,
          padding: "9px 14px", borderRadius: 10,
          textDecoration: "none", transition: "background 0.18s",
          letterSpacing: "0.02em",
        }}
        onMouseEnter={e => e.currentTarget.style.background = data.accent + "15"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        Send an Email
        <ArrowIcon />
      </a>
    </motion.div>
  );
}

/* ─── MAIN PAGE ────────────────────────────────────────────────────────────── */
export default function Contact() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  useEffect(() => {
    const scriptSrc = "https://tally.so/widgets/embed.js";
    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.async = true;
      document.body.appendChild(script);
    } else if (window.Tally) {
      window.Tally.loadEmbeds();
    }
  }, []);

  // Explicit tokens
  const pageBg      = isDark ? "#09090f"                : "#F8F9FB";
  const heroBorder  = isDark ? "rgba(255,255,255,0.07)" : "#E5E7EB";
  const heroGlow    = isDark ? "rgba(2,142,196,0.11)"   : "rgba(2,142,196,0.06)";
  const textPri     = isDark ? "rgba(255,255,255,0.93)" : "#111827";
  const textMuted   = isDark ? "rgba(255,255,255,0.48)" : "#6B7280";
  const pillBg      = isDark ? "rgba(2,142,196,0.12)"   : "rgba(2,142,196,0.08)";
  const pillBorder  = isDark ? "rgba(2,142,196,0.35)"   : "rgba(2,142,196,0.22)";
  const statBg      = isDark ? "rgba(255,255,255,0.04)" : "#ffffff";
  const statBorder  = isDark ? "rgba(255,255,255,0.08)" : "#E5E7EB";
  const formBg      = isDark ? "#16161f"                : "#ffffff";
  const formBorder  = isDark ? "rgba(255,255,255,0.09)" : "#E5E7EB";
  const formShadow  = isDark ? "0 8px 40px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.08)";
  const sectionBg   = isDark ? "rgba(255,255,255,0.03)" : "rgba(2,142,196,0.03)";
  const sectionBord = isDark ? "rgba(255,255,255,0.07)" : "rgba(2,142,196,0.12)";
  const promiseBg   = isDark ? "rgba(16,185,129,0.07)"  : "rgba(16,185,129,0.05)";
  const promiseBord = isDark ? "rgba(16,185,129,0.22)"  : "rgba(16,185,129,0.2)";

  return (
    <>
      <style>{`
        ${FONT_LINK}
        * { box-sizing: border-box; }
        .ct-page { font-family: 'DM Sans', sans-serif; }

        /* Form + sidebar grid — stacks on mobile */
        .ct-form-grid {
          display: grid;
          grid-template-columns: 1fr 340px;
          gap: 28px;
          margin-top: 48px;
          align-items: start;
        }
        @media (max-width: 768px) {
          .ct-form-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          /* Sidebar drops below the form on mobile */
          .ct-form-col  { order: 1; }
          .ct-sidebar   { order: 2; }
          /* Shrink hero padding on mobile */
          .ct-hero { padding: 52px 20px 44px !important; }
          /* Tighten the page outer padding on mobile */
          .ct-inner { padding: 0 16px !important; }
          /* Email channel cards: full width on mobile */
          .ct-channels-grid {
            grid-template-columns: 1fr !important;
          }
          /* Footer note: full width on mobile */
          .ct-footer-note {
            display: flex !important;
            width: 100% !important;
            padding: 24px 20px !important;
          }
        }
      `}</style>

      <div className="ct-page" style={{ background: pageBg, minHeight: "100vh", padding: "0 0 100px", color: textPri }}>

        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <div className="ct-hero" style={{
          textAlign: "center", padding: "80px 24px 64px",
          background: `radial-gradient(ellipse 80% 45% at 50% 0%, ${heroGlow} 0%, transparent 70%)`,
          borderBottom: `1px solid ${heroBorder}`,
        }}>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              background: pillBg, border: `1px solid ${pillBorder}`,
              color: "#028EC4", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em",
              textTransform: "uppercase", padding: "6px 16px", borderRadius: 999, marginBottom: 22,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#028EC4" }} />
              We'd Love to Hear From You
            </span>

            <h1 style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: "clamp(2.8rem, 7vw, 5rem)", fontWeight: 800,
              lineHeight: 1.06, margin: "0 0 18px", color: textPri,
            }}>
              Let's{" "}
              <span style={{ background: "linear-gradient(135deg, #028EC4, #38BDF8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Talk
              </span>
            </h1>

            <p style={{
              fontSize: "clamp(14px, 2vw, 17px)", color: textMuted,
              maxWidth: 560, margin: "0 auto 40px", lineHeight: 1.8,
            }}>
              Whether you're a startup looking to hire intern talent, a partner exploring collaboration, or a learner seeking opportunities — we're always happy to connect.
            </p>

            {/* Stats */}
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px 10px" }}>
              {[
                ["⚡", "24h", "Response Promise"],
                ["🌐", "6+", "Contact Channels"],
                ["🤝", "50+", "Partner Companies"],
                ["💬", "6,000+", "Learners Supported"],
              ].map(([icon, v, l]) => (
                <div key={l} style={{ textAlign: "center", padding: "11px 20px", background: statBg, border: `1px solid ${statBorder}`, borderRadius: 12, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 18 }}>{icon}</span>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1rem, 2vw, 1.3rem)", fontWeight: 800, color: textPri, lineHeight: 1 }}>{v}</div>
                    <div style={{ fontSize: 11.5, color: textMuted, marginTop: 2 }}>{l}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="ct-inner" style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>

          {/* ── 24h PROMISE BANNER ────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.45 }}
            style={{ marginTop: 56 }}
          >
            <div style={{
              background: promiseBg, border: `1px solid ${promiseBord}`,
              borderRadius: 16, padding: "18px 24px",
              display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
            }}>
              <div style={{
                width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.25)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#10B981",
              }}>
                <ClockIcon />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: 15, color: textPri, margin: "0 0 3px" }}>
                  Our team promises to respond within 24 hours ⚡
                </p>
                <p style={{ fontSize: 13, color: textMuted, margin: 0, lineHeight: 1.6 }}>
                  Whether it's a form submission or a direct email — we take every message seriously and aim to get back to you the very same day.
                </p>
              </div>
              <span style={{
                background: "rgba(16,185,129,0.15)", color: "#10B981",
                fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
                textTransform: "uppercase", padding: "5px 12px", borderRadius: 999,
                border: "1px solid rgba(16,185,129,0.3)", flexShrink: 0,
              }}>
                ● Active
              </span>
            </div>
          </motion.div>

          {/* ── FORM + SIDEBAR LAYOUT ─────────────────────────────────────────── */}
          <div className="ct-form-grid">

            {/* Form */}
            <motion.div
              className="ct-form-col"
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.48 }}
            >
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 6px" }}>
                  General Enquiry
                </p>
                <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 800, color: textPri, margin: "0 0 7px" }}>
                  Send Us a Message
                </h2>
                <p style={{ color: textMuted, fontSize: 14, margin: 0 }}>
                  Fill in the form below and we'll route your message to the right team.
                </p>
              </div>
              <div style={{
                background: formBg, border: `1px solid ${formBorder}`,
                borderRadius: 20, overflow: "hidden", boxShadow: formShadow,
              }}>
                <iframe
                  data-tally-src="https://tally.so/embed/obeQab?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1"
                  loading="lazy"
                  width="100%"
                  height="560"
                  frameBorder="0"
                  marginHeight="0"
                  marginWidth="0"
                  title="Let's Talk – GrapplTech"
                  style={{ border: "none", display: "block", padding: "8px 0" }}
                />
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div
              className="ct-sidebar"
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.48, delay: 0.08 }}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              {/* Quick info */}
              <div style={{
                background: sectionBg, border: `1px solid ${sectionBord}`,
                borderRadius: 16, padding: "22px 20px",
              }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 14px" }}>
                  What to Expect
                </p>
                {[
                  ["📩", "Your message lands in our inbox instantly"],
                  ["⚡", "We respond within 24 hours — usually sooner"],
                  ["🎯", "Your message is routed to the right team"],
                  ["🔒", "Your details are never shared with third parties"],
                ].map(([icon, text]) => (
                  <div key={text} style={{ display: "flex", gap: 10, marginBottom: 11, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                    <span style={{ fontSize: 13, color: textMuted, lineHeight: 1.6 }}>{text}</span>
                  </div>
                ))}
              </div>

              {/* Office info */}
              <div style={{
                background: formBg, border: `1px solid ${formBorder}`,
                borderRadius: 16, padding: "22px 20px",
              }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#F59E0B", margin: "0 0 14px" }}>
                  Based in India
                </p>
                {[
                  ["🕐", "Mon–Sat, 9 AM – 7 PM IST"],
                  ["🌏", "Remote-first team across India & Canada"],
                  ["🎓", "Serving learners in 12+ cities"],
                ].map(([icon, text]) => (
                  <div key={text} style={{ display: "flex", gap: 10, marginBottom: 11, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                    <span style={{ fontSize: 13, color: textMuted, lineHeight: 1.6 }}>{text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* ── EMAIL CHANNELS GRID ───────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.48 }}
            style={{ marginTop: 72 }}
          >
            <div style={{ marginBottom: 28 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: "#028EC4", margin: "0 0 6px" }}>
                Direct Contact
              </p>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 800, color: textPri, margin: "0 0 7px" }}>
                    Reach the Right Team
                  </h2>
                  <p style={{ color: textMuted, fontSize: 14, margin: 0, maxWidth: 480 }}>
                    Every channel has a dedicated team behind it. Click the email address to copy it, or hit "Send an Email" to open your mail client directly.
                  </p>
                </div>
                <div style={{
                  display: "flex", alignItems: "center", gap: 7,
                  background: promiseBg, border: `1px solid ${promiseBord}`,
                  borderRadius: 10, padding: "8px 14px",
                }}>
                  <span style={{ color: "#10B981" }}><ClockIcon /></span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: textPri }}>All teams reply within 24h</span>
                </div>
              </div>
            </div>

            <div className="ct-channels-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 18 }}>
              {CHANNELS.map((ch, i) => (
                <motion.div key={ch.label} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                  <ChannelCard data={ch} isDark={isDark} />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ── FOOTER NOTE ───────────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.45 }}
            style={{ marginTop: 56, textAlign: "center" }}
          >
            <div className="ct-footer-note" style={{
              display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 10,
              background: sectionBg, border: `1px solid ${sectionBord}`,
              borderRadius: 16, padding: "24px 36px",
            }}>
              <span style={{ fontSize: 28 }}>💙</span>
              <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, fontSize: 16, color: textPri, margin: 0 }}>
                Every message matters to us.
              </p>
              <p style={{ fontSize: 13.5, color: textMuted, margin: 0, maxWidth: 420, lineHeight: 1.7, textAlign: "center" }}>
                We're a small, passionate team building something meaningful. Your feedback, ideas, and questions genuinely shape how we grow. Don't hesitate to reach out.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </>
  );
}